import React, { useState, useCallback, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import ContactList from './components/ContactList';
import ChatWindow from './components/ChatWindow';
import RightPanel from './components/RightPanel';
import BlastModal from './components/BlastModal';
import SettingsPanel from './components/SettingsPanel';
import WorkspaceSelector from './components/WorkspaceSelector';
import {
  INITIAL_COMPANIES,
  INITIAL_INSTANCES,
  INITIAL_CONTACTS,
  INITIAL_MESSAGES,
} from './config';

// ── SUPABASE ──────────────────────────────────────────────────────────────────
const SB_URL = 'https://zixiwkbiqnrkxxjeyslq.supabase.co';
const SB_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InppeGl3a2JpcW5ya3h4amV5c2xxIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc1ODA0MDkyNSwiZXhwIjoyMDczNjE2OTI1fQ.w6xu1WTUaAgQ9mWG2KjZt59-EwPaR6Wq8vx6nMlqEog';
const SB_HEADERS = { apikey: SB_KEY, Authorization: `Bearer ${SB_KEY}` };

async function sbFetch(path) {
  try {
    const res = await fetch(`${SB_URL}/rest/v1/${path}`, { headers: SB_HEADERS });
    return res.json();
  } catch { return []; }
}

function formatTime(ts) {
  const d = new Date(ts);
  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function App() {
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [nav, setNav] = useState('chat');
  const [activeContactId, setActiveContactId] = useState(null);
  const [contacts, setContacts] = useState(INITIAL_CONTACTS);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [companies, setCompanies] = useState(INITIAL_COMPANIES);
  const [instances, setInstances] = useState(INITIAL_INSTANCES);
  const [showBlast, setShowBlast] = useState(false);

  const activeContactIdRef = useRef(activeContactId);
  activeContactIdRef.current = activeContactId;
  const wsRef = useRef(null);

  // ── SUPABASE REALTIME ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeCompanyId) return;

    const companyInstances = instances.filter(i => i.company === activeCompanyId);
    const instanceNames = companyInstances.map(i => i.name);

    // 1. Carrega histórico de contatos reais
    sbFetch('omni_messages?select=phone,instance,text,timestamp&order=timestamp.desc&limit=500')
      .then(rows => {
        if (!Array.isArray(rows)) return;
        const seen = {};
        rows.filter(r => instanceNames.includes(r.instance))
          .forEach(r => { if (!seen[r.phone]) seen[r.phone] = r; });

        const newContacts = Object.values(seen).map(r => {
          const inst = companyInstances.find(i => i.name === r.instance);
          return {
            id: r.phone,
            name: r.phone,
            phone: r.phone,
            company: activeCompanyId,
            instance: inst?.id || '',
            channel: 'whatsapp',
            tags: [],
            stage: 'novo',
            agentActive: true,
            unread: 0,
            lastMsg: r.text,
            lastTime: formatTime(r.timestamp),
          };
        });

        if (newContacts.length > 0) {
          setContacts(prev => {
            const merged = [...prev];
            newContacts.forEach(nc => {
              if (!merged.find(c => c.id === nc.id)) merged.push(nc);
            });
            return merged;
          });
        }
      });

    // 2. Carrega mensagens do contato ativo se existir
    if (activeContactId) {
      sbFetch(`omni_messages?phone=eq.${activeContactId}&order=timestamp.asc&limit=100`)
        .then(rows => {
          if (!Array.isArray(rows) || rows.length === 0) return;
          const msgs = rows.map(r => ({
            id: r.id,
            from: r.from_me ? 'bot' : 'contact',
            text: r.text,
            time: formatTime(r.timestamp),
            type: 'text',
          }));
          setMessages(prev => ({ ...prev, [activeContactId]: msgs }));
        });
    }

    // 3. WebSocket Realtime
    const wsUrl = `${SB_URL.replace('https', 'wss')}/realtime/v1/websocket?apikey=${SB_KEY}&vsn=1.0.0`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        topic: 'realtime:public:omni_messages',
        event: 'phx_join',
        payload: {
          config: {
            postgres_changes: [{ event: 'INSERT', schema: 'public', table: 'omni_messages' }],
          },
        },
        ref: '1',
      }));
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        const record = msg.payload?.data?.record;
        if (!record || !instanceNames.includes(record.instance)) return;

        const phone = record.phone;
        const time = formatTime(record.timestamp);
        const newMsg = {
          id: record.id,
          from: record.from_me ? 'bot' : 'contact',
          text: record.text,
          time,
          type: 'text',
        };

        setMessages(prev => ({
          ...prev,
          [phone]: [...(prev[phone] || []), newMsg],
        }));

        setContacts(prev => {
          const exists = prev.find(c => c.id === phone);
          const isActive = activeContactIdRef.current === phone;
          if (exists) {
            return prev.map(c => c.id === phone
              ? { ...c, lastMsg: record.text, lastTime: time, unread: isActive ? 0 : (c.unread || 0) + 1 }
              : c
            );
          }
          const inst = companyInstances.find(i => i.name === record.instance);
          return [...prev, {
            id: phone, name: phone, phone,
            company: activeCompanyId,
            instance: inst?.id || '',
            channel: 'whatsapp',
            tags: [], stage: 'novo',
            agentActive: true,
            unread: 1,
            lastMsg: record.text,
            lastTime: time,
          }];
        });
      } catch {}
    };

    return () => { ws.close(); };
  }, [activeCompanyId, instances]);

  // ── HANDLERS ──────────────────────────────────────────────────────────────

  const handleAddCompany = useCallback((data) => {
    const id = data.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setCompanies(prev => [...prev, { id, ...data }]);
  }, []);

  const handleSelectWorkspace = useCallback((companyId) => {
    setActiveCompanyId(companyId);
    setActiveContactId(null);
    setNav('chat');
  }, []);

  const handleSelectContact = useCallback((id) => {
    setActiveContactId(id);
    setNav('chat');
    setContacts(prev => prev.map(c => c.id === id ? { ...c, unread: 0 } : c));
    // Carrega histórico do contato
    sbFetch(`omni_messages?phone=eq.${id}&order=timestamp.asc&limit=100`)
      .then(rows => {
        if (!Array.isArray(rows) || rows.length === 0) return;
        const msgs = rows.map(r => ({
          id: r.id,
          from: r.from_me ? 'bot' : 'contact',
          text: r.text,
          time: formatTime(r.timestamp),
          type: 'text',
        }));
        setMessages(prev => ({ ...prev, [id]: msgs }));
      });
  }, []);

  const handleSendMessage = useCallback((contactId, text) => {
    const now = new Date();
    const time = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    setMessages(prev => ({
      ...prev,
      [contactId]: [...(prev[contactId] || []), { id: Date.now(), from: 'me', text, time, type: 'text' }],
    }));
    setContacts(prev => prev.map(c => c.id === contactId ? { ...c, lastMsg: text, lastTime: time } : c));
  }, []);

  const handleUpdateContact = useCallback((id, patch) => {
    setContacts(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  }, []);

  const handleNavChange = (section) => {
    if (section === 'blast') { setShowBlast(true); return; }
    setNav(section);
  };

  // ── TELA DE SELEÇÃO ───────────────────────────────────────────────────────
  if (!activeCompanyId) {
    return (
      <WorkspaceSelector
        companies={companies.map(c => ({
          ...c,
          instanceCount: instances.filter(i => i.company === c.id).length,
        }))}
        onSelect={handleSelectWorkspace}
        onAddCompany={handleAddCompany}
      />
    );
  }

  // ── APP PRINCIPAL ─────────────────────────────────────────────────────────
  const companyContacts = contacts.filter(c => c.company === activeCompanyId);
  const activeContact = contacts.find(c => c.id === activeContactId);
  const activeMessages = messages[activeContactId] || [];
  const totalUnread = companyContacts.reduce((sum, c) => sum + (c.unread || 0), 0);

  const s = {
    app: { display: 'flex', height: '100vh', overflow: 'hidden', background: '#0f0f11' },
    chatLayout: { flex: 1, display: 'flex', overflow: 'hidden' },
  };

  return (
    <div style={s.app}>
      <Sidebar
        active={nav}
        onChange={handleNavChange}
        unreadCount={totalUnread}
        company={companies.find(c => c.id === activeCompanyId)}
        onSwitchWorkspace={() => setActiveCompanyId(null)}
      />

      {nav === 'settings' ? (
        <SettingsPanel
          companies={companies}
          instances={instances}
          onUpdateCompanies={setCompanies}
          onUpdateInstances={setInstances}
          companyId={activeCompanyId}
        />
      ) : (
        <div style={s.chatLayout}>
          <ContactList
            contacts={companyContacts}
            activeId={activeContactId}
            onSelect={handleSelectContact}
            companies={companies}
          />
          <ChatWindow
            contact={activeContact}
            messages={activeMessages}
            onSend={handleSendMessage}
            companies={companies}
            instances={instances}
          />
          <RightPanel
            contact={activeContact}
            onUpdate={handleUpdateContact}
            companies={companies}
            instances={instances}
          />
        </div>
      )}

      {showBlast && (
        <BlastModal
          onClose={() => setShowBlast(false)}
          instances={instances}
          companies={companies}
          companyId={activeCompanyId}
        />
      )}
    </div>
  );
}
